import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UserGroup } from "../../../model/userGroup";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { MatChipInputEvent } from "@angular/material/chips";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { PermissionService } from "../../../service/permission.service";
import { UserGroupInvite } from "../../../model/userGroupInvite";

@Component({
  selector: 'app-edit-user-group-dialog',
  templateUrl: './edit-user-group-dialog.component.html',
  styleUrls: ['./edit-user-group-dialog.component.css']
})
export class EditUserGroupDialogComponent implements OnInit {

  newFormData: UntypedFormGroup;
  editFormData: UntypedFormGroup;
  invites: UserGroupInvite[] = [];
  disableEditFormTab: boolean = false;
  disableCreateFormTab: boolean = false;
  private deleteTimer: any;
  public deleteInProgress: boolean = false;
  public progressValue: number = 0;
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  emails: String[] = [];
  constructor(
      public dialogRef: MatDialogRef<EditUserGroupDialogComponent>,
      private formBuilder: UntypedFormBuilder,
      private permissionService: PermissionService,
      @Inject(MAT_DIALOG_DATA) public data: {groups: UserGroup[], active: number, invites: UserGroupInvite[], hideEdit: boolean, hideCreate: boolean},
  ) {
    this.newFormData = this.formBuilder.group({
      name: '',
      role_admin: false,
      can_order: false,
      can_view: false,
      can_download: false,
      permissions: -1
    });

    this.editFormData = this.formBuilder.group({
      name: this.data.groups[this.data.active].name,
      role_admin: this.data.groups[this.data.active].roles.includes("ADMIN"),
      role_user: this.data.groups[this.data.active].roles.includes("USER"),
      can_order: this.data.groups[this.data.active].can_order,
      can_download: this.data.groups[this.data.active].can_download,
      can_view: this.data.groups[this.data.active].can_view
    });
  }

  ngOnInit(): void {
    // enable/disable parts of the modal
    this.disableEditFormTab = this.data.hideEdit;
    this.disableCreateFormTab = this.data.hideCreate;

    // have invites been passed or do we need to fetch them?
    if(this.data.invites) {
      this.invites = this.data.invites;
    } else {
      this.permissionService.getUserGroupInvites(this.data.groups[this.data.active].id).subscribe(result => {
        this.invites = result;
        this.invites.forEach(invite => {
          // manually update the status based on the expired date
          if(new Date(invite.expires) < new Date() && invite.status !== 'COMPLETE') {
            invite.status = 'EXPIRED';
          }
        })
      }, error => {
        console.log(error);
      });
    }
  }

  createGroup(): void {
    const group = new UserGroup(null);
    group.name = this.newFormData.value['name'];
    group.companyLoginId  = this.data.groups[0].companyLoginId;
    group.can_download = this.newFormData.value['can_download'];
    group.can_view = this.newFormData.value['can_view'];
    group.can_order = this.newFormData.value['can_order'];
    group.roles = ["USER"];
    if(this.newFormData.value['role_admin']) {
      group.roles.push("ADMIN");
    }

    if(this.newFormData.value['permissions'] != -1) {
      group.permissions = new Map();
      this.data.groups[this.newFormData.value['permissions']].permissions.forEach((entry, key) => {
        group.permissions.set(key, entry);
      });
    } else {
      group.permissions = null;
    }

    this.dialogRef.close({action: "create", data: group, invites: {send: true, mails: this.emails}});
  }

  editGroup(): void {
    let currentGroup = this.data.groups[this.data.active];
    currentGroup.name = this.editFormData.value['name'];
    currentGroup.can_view = this.editFormData.value['can_view'];
    currentGroup.can_download = this.editFormData.value['can_download'];
    currentGroup.can_order = this.editFormData.value['can_order'];
    currentGroup.roles = [];

    if(this.editFormData.value['role_user']) {
      currentGroup.roles.push('USER');
    } else {
      currentGroup.roles = currentGroup.roles.filter(role => role !== 'USER');
    }

    if(this.editFormData.value['role_admin']) {
      currentGroup.roles.push('ADMIN');
    } else {
      currentGroup.roles = currentGroup.roles.filter(role => role !== 'ADMIN');
    }

    this.dialogRef.close({action: "edit", data: currentGroup});
  }

  delete(): void {
    this.dialogRef.close( {action: "delete", data: this.data.groups[this.data.active]});
  }

  cancel(): void {
    this.dialogRef.close();
  }

  copyUserGroupData() {
    const index = this.newFormData.controls['permissions'].value;
    if(index != -1) {
      const copy: UserGroup = this.data.groups[index];
      this.updateNewFormWithCopy(copy);
    } else {
      this.updateNewFormWithCopy(null);
    }
  }

  updateNewFormWithCopy(copyUserGroup: UserGroup | null): void {
    if(copyUserGroup) {
      this.newFormData.controls['role_admin'].setValue(copyUserGroup.roles.includes("ADMIN"));
      this.newFormData.controls['can_order'].setValue(copyUserGroup.can_order);
      this.newFormData.controls['can_view'].setValue(copyUserGroup.can_view);
      this.newFormData.controls['can_download'].setValue(copyUserGroup.can_download);
    } else {
      this.newFormData.controls['role_admin'].setValue(false);
      this.newFormData.controls['can_order'].setValue(false);
      this.newFormData.controls['can_view'].setValue(false);
      this.newFormData.controls['can_download'].setValue(false);
    }
  }

  add(event: MatChipInputEvent): void {
    const mails = event.value.split(/[; ]+/);
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    mails.forEach(mail => {
      mail.trim();
      if(pattern.test(mail) && !this.emails.includes(mail) ) {
        this.emails.push(mail);
      }
    });

    // Clear the input value
    event.input.value = '';
  }

  remove(email: String): void {
    const index = this.emails.indexOf(email);

    if (index >= 0) {
      this.emails.splice(index, 1);
    }
  }

  startTimer() {
    this.deleteInProgress = true;
    this.progressValue = 0;

    const duration = 1000; // Duration in milliseconds (1 second)
    const interval = 10; // Interval in milliseconds for updating the progress
    const totalSteps = duration / interval;
    const increment = 100 / totalSteps;

    let currentStep = 0;
    this.deleteTimer = setInterval( () => {
      this.progressValue += increment;
      currentStep++;

      if (currentStep >= totalSteps) {
        clearInterval(this.deleteTimer);
        this.progressValue = 100; // Ensure the progress reaches 100% at the end
        this.delete();
      }
    }, interval);
  }

  cancelTimer() {
    clearInterval(this.deleteTimer);
    this.deleteInProgress = false;
  }

  getTooltip(): string {
    return "Zum Löschen 1 Sekunde gedrückt halten";
  }
}
